import { Component, DestroyRef, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DatePipe } from '@angular/common';
import { Router } from '@angular/router';
import { FieldDashboardDataService, loadedFieldContext } from '../field-dashboard-data.service';
import { FieldContext } from '../../models/field-context';
import { LinkTab } from './field-link-editor/field-link-editor.component';
import { DashboardOrigin } from '../field-dashboard-nav.service';
import { FieldDashboardViewComponent, INTEGRATION_STATUS_BUTTON_ID } from '../field-dashboard-view.component';
import { seasonLabel } from '../macronutrients/season-period';

@Component({
  selector: 'app-field-analysis',
  templateUrl: './field-analysis.component.html',
  styleUrls: ['./field-analysis.component.css'],
  standalone: false
})
export class FieldAnalysisComponent implements OnInit {
  fieldId: string;
  farmId: string;
  farmName: string = '';
  fieldName: string = '';
  crop: string = '';
  cultivar: string = '';
  seasonYear: string = '';
  fieldInfoLoading: boolean = true;
  /** Null until the sync service answers, or if the call failed. */
  fieldContext: FieldContext | null = null;
  fieldContextLoading: boolean = true;
  /** Built when the context arrives rather than from the template, to keep it off every cycle. */
  restBreakTag: string = '';
  /** Kept apart from a null context so a failed call is not rendered as "not linked". */
  fieldContextError: boolean = false;
  linkEditorOpen: boolean = false;
  linkEditorTab: LinkTab = 'fms';
  /** Where Map Analysis goes back to. */
  origin: DashboardOrigin = 'mapping';
  /** Hover text per tap: how it was linked, and by whom when it was by hand. */
  tapTitles: Record<string, string> = {};

  // en-US is always registered; the explicit yyyy-MM-dd format is what sets the output.
  private datePipe = new DatePipe('en-US');
  private readonly destroyRef = inject(DestroyRef);
  /**
   * The dashboard page this card is routed inside. It owns the integration status and its drawer; the
   * card only shows the "?" button beside the field outline (Heinrich, 28/10/2026). Null outside it.
   */
  readonly dashboard = inject(FieldDashboardViewComponent, { optional: true });
  readonly statusButtonId = INTEGRATION_STATUS_BUTTON_ID;

  constructor(
    private router: Router,
    private dataService: FieldDashboardDataService
  ) { }

  ngOnInit(): void {
    const now = new Date();
    const year = now.getMonth() >= 5 ? now.getFullYear() + 1 : now.getFullYear();
    this.seasonYear = seasonLabel(year - 1);

    this.dataService.fieldData$.subscribe(data => {
      this.fieldId = data.fieldId;
      this.farmId = data.farmId;
      this.origin = data.from === 'maps' ? 'maps' : 'mapping';
      this.fieldName = data.fieldName;
      this.farmName = data.farmName || '';
      this.crop = data.crop || '';
      this.cultivar = data.cultivar || '';
      if (data.crop || data.cultivar) {
        this.fieldInfoLoading = false;
      }
      if (data.fieldInfoLoading !== undefined) {
        this.fieldInfoLoading = data.fieldInfoLoading;
      }
      if (data.season) {
        this.seasonYear = data.season;
      }
      if (data.fieldContext !== undefined) {
        this.fieldContext = data.fieldContext;
        this.restBreakTag = this.buildRestBreakTag();
        this.tapTitles = this.buildTapTitles();
      }
      if (data.fieldContextLoading !== undefined) {
        this.fieldContextLoading = data.fieldContextLoading;
      }
      if (data.fieldContextError !== undefined) {
        this.fieldContextError = data.fieldContextError;
      }
    });

    // "Edit FMS link" and "Edit iGator taps" in the integration drawer open this page's editor, under
    // the same rule as this page's own link rows: only on a context loaded for this field.
    this.dataService.linkEditorRequest$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(tab => {
        if (loadedFieldContext(this.dataService.getFieldData())) {
          this.openLinkEditor(tab);
        }
      });
    this.destroyRef.onDestroy(() => this.dataService.linkEditorOpen.set(false));
  }

  /**
   * How the rest break went against the plan. The gap is the part worth acting on: the dates
   * differ for roughly half to two thirds of fields each season, so "applied" on its own hides
   * whether the block was broken on time.
   */
  private buildRestBreakTag(): string {
    const restBreak = this.fieldContext?.restBreak;
    if (!restBreak) {
      return '';
    }
    if (!restBreak.actualDate) {
      return 'Planned';
    }
    const days = this.dayDelta(restBreak.plannedDate, restBreak.actualDate);
    if (days === null) {
      return 'Applied';
    }
    if (days === 0) {
      return 'Applied on plan';
    }
    const magnitude = Math.abs(days);
    const unit = magnitude === 1 ? 'day' : 'days';
    return `Applied ${magnitude} ${unit} ${days > 0 ? 'late' : 'early'}`;
  }

  /** Whole days between two yyyy-MM-dd values, compared in UTC so a local offset cannot shift it. */
  private dayDelta(planned: string | null, actual: string | null): number | null {
    if (!planned || !actual) {
      return null;
    }
    const from = Date.parse(planned.substring(0, 10) + 'T00:00:00Z');
    const to = Date.parse(actual.substring(0, 10) + 'T00:00:00Z');
    if (isNaN(from) || isNaN(to)) {
      return null;
    }
    return Math.round((to - from) / 86400000);
  }

  openLinkEditor(tab: LinkTab): void {
    this.linkEditorTab = tab;
    this.linkEditorOpen = true;
    this.dataService.linkEditorOpen.set(true);
  }

  onLinkEditorClosed(saved: boolean): void {
    this.linkEditorOpen = false;
    this.dataService.linkEditorOpen.set(false);
    if (saved) {
      this.dataService.requestFieldContextReload();
    }
  }

  private buildTapTitles(): Record<string, string> {
    const titles: Record<string, string> = {};
    for (const d of this.fieldContext?.igator?.tapDetails ?? []) {
      if (d.source === 'MANUAL') {
        const who = d.linkedBy?.userName || 'unknown user';
        const when = d.linkedBy?.at ? this.datePipe.transform(d.linkedBy.at, 'yyyy-MM-dd HH:mm') : '';
        titles[d.tap] = `Linked by hand by ${who}${when ? ' on ' + when : ''}`;
      } else {
        titles[d.tap] = 'Linked automatically by the sync';
      }
    }
    return titles;
  }

  /** Back to the map page the user came from: the new maps field view, or the old mapping field page. */
  navigateToFieldAnalysis(): void {
    if (this.origin === 'maps') {
      this.router.navigate(['/maps/farm', this.farmId, 'field', this.fieldId]);
      return;
    }
    this.router.navigate(['/field'], {
      state: {
        id: this.fieldId,
        farmId: this.farmId,
        farmName: this.farmName,
        fieldName: this.fieldName,
        analysisTypes: '',
        analysisDates: ''
      }
    });
  }
}
